import React, { createContext, useContext, useState, useCallback } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmDialog - Modal de confirmacion reutilizable
 *
 * Reemplaza window.confirm() y alert() con un modal estetico
 *
 * Uso:
 *   const { confirm, alert } = useConfirm();
 *
 *   // Para confirmaciones
 *   const confirmed = await confirm({
 *     title: 'Eliminar elemento',
 *     message: '¿Estas seguro?',
 *     confirmText: 'Eliminar',
 *     cancelText: 'Cancelar',
 *     type: 'danger' // 'danger' | 'warning' | 'info' | 'success'
 *   });
 *
 *   // Para alertas (solo OK)
 *   await alert({
 *     title: 'Operacion completada',
 *     message: 'Los datos fueron guardados',
 *     type: 'success'
 *   });
 */

// Context
const ConfirmContext = createContext(null);

// Hook para usar el contexto
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

// Configuracion de tipos
const typeConfig = {
  danger: {
    icon: ExclamationTriangleIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    confirmText: 'text-white'
  },
  warning: {
    icon: ExclamationCircleIcon,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    confirmBg: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    confirmText: 'text-white'
  },
  info: {
    icon: InformationCircleIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    confirmBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    confirmText: 'text-white'
  },
  success: {
    icon: CheckCircleIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    confirmBg: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    confirmText: 'text-white'
  }
};

// Componente del Modal
const ConfirmModal = ({ isOpen, options, onConfirm, onCancel }) => {
  const { t } = useTranslation('common');
  if (!isOpen) return null;

  const {
    title = t('confirmDialog.defaultTitle'),
    message = t('confirmDialog.defaultMessage'),
    confirmText = t('accept'),
    cancelText = t('cancel'),
    type = 'warning',
    isAlert = false, // Si es true, solo muestra boton OK
    details = null   // Detalles adicionales (lista de items)
  } = options;

  const config = typeConfig[type] || typeConfig.warning;
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto" data-testid="mdl-confirmar">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={isAlert ? onConfirm : onCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
            <div className="sm:flex sm:items-start">
              {/* Icon */}
              <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${config.iconBg} sm:mx-0 sm:h-10 sm:w-10`}>
                <IconComponent className={`h-6 w-6 ${config.iconColor}`} aria-hidden="true" />
              </div>

              {/* Content */}
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">
                    {title}
                  </h3>
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={isAlert ? onConfirm : onCancel}
                  >
                    <span className="sr-only">{t('confirmDialog.closeAriaLabel')}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-2">
                  <p className="text-sm text-gray-500 whitespace-pre-wrap">
                    {message}
                  </p>

                  {/* Detalles adicionales */}
                  {details && (
                    <div className="mt-3 bg-gray-50 rounded-md p-3 max-h-32 overflow-y-auto">
                      {Array.isArray(details) ? (
                        <ul className="text-sm text-gray-600 space-y-1">
                          {details.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-600">{details}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
            <button
              type="button"
              className={`inline-flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm sm:w-auto ${config.confirmBg} ${config.confirmText} focus:outline-none focus:ring-2 focus:ring-offset-2`}
              onClick={onConfirm}
              data-testid="btn-confirmar-aceptar"
            >
              {confirmText}
            </button>

            {!isAlert && (
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                onClick={onCancel}
                data-testid="btn-confirmar-cancelar"
              >
                {cancelText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Provider
export const ConfirmProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState({});
  const [resolvePromise, setResolvePromise] = useState(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setOptions({ ...opts, isAlert: false });
      setResolvePromise(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const alert = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setOptions({
        ...opts,
        isAlert: true,
        confirmText: opts.confirmText || undefined
      });
      setResolvePromise(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(true);
    }
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(false);
    }
  }, [resolvePromise]);

  return (
    <ConfirmContext.Provider value={{ confirm, alert }}>
      {children}
      <ConfirmModal
        isOpen={isOpen}
        options={options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

// Default export for convenience
export default ConfirmProvider;
